import { create } from 'zustand';

import { getBizyPanelMode } from '../lib/panel-preferences';

type BizySidePanelView = 'chat' | 'history';
export const BIZY_SETTINGS_SECTIONS = ['general', 'memories', 'notifications', 'connections', 'apps'] as const;
export type BizySettingsSection = (typeof BIZY_SETTINGS_SECTIONS)[number];

export type BizyPanelTrigger = 'auto' | 'user' | 'keyboard' | 'navbar_restore' | 'navbar_collapse' | (string & {});

// Set as lastTrigger by useBizySettingsDeepLink when `?bizy_settings=<section>`
// opens the panel. While it's the lastTrigger and the settings modal is still
// open, 'auto' closes no-op (see `close`) so later mount effects — the
// sessionStorage restore on Rails admin pages and the meeting-route preference
// close in useBizyLayout — can't undo the deep link. Any user interaction
// overwrites lastTrigger, ending the protection.
export const BIZY_SETTINGS_DEEP_LINK_TRIGGER = 'settings_deep_link';

export type BizySupportMode = 'bizy' | 'fin' | 'human_agent';

export interface BizySupportContext {
	summary?: string;
	category?: string;
	finConversationId?: string;
}

interface BizySidePanelOpenOptions {
	isFullscreen?: boolean;
	isHistoryDrawerOpen?: boolean;
}

export const BIZY_PANEL_DEFAULT_WIDTH = 352;
export const BIZY_PANEL_MIN_WIDTH = 280;
export const BIZY_PANEL_MAX_WIDTH = 560;
export const BIZY_FULLSCREEN_DRAWER_DEFAULT_WIDTH = 292;
export const BIZY_FULLSCREEN_DRAWER_MIN_WIDTH = 240;
export const BIZY_FULLSCREEN_DRAWER_MAX_WIDTH = 440;

interface BizySidePanelStore {
	isOpen: boolean;
	isFullscreen: boolean;
	lastTrigger: BizyPanelTrigger | null;
	view: BizySidePanelView;
	isHistoryDrawerOpen: boolean;
	fullscreenHistoryDrawerOpen: boolean;
	isSettingsModalOpen: boolean;
	settingsSection: BizySettingsSection;
	fullscreenDrawerWidth: number;
	width: number;
	isResizing: boolean;
	supportMode: BizySupportMode;
	supportContext: BizySupportContext;
	// Bumped by `openFromNotificationPeek` so the coordinator hook can
	// pick up an engagement signal even when the panel is already open
	// (where the close→open snapshot pattern would otherwise no-op).
	// Drives the "click Ask Bizy with unread + panel already open"
	// path: previously this just toggled the panel closed; now it surfaces
	// the proactive conversation.
	engageRequestVersion: number;
	open: (trigger?: BizyPanelTrigger, options?: BizySidePanelOpenOptions) => void;
	// Opens the panel via the notification-peek path. Sets lastTrigger so
	// useLoadProactiveConversationOnOpen knows to auto-load the linked
	// proactive conversation and mark it seen. Bumps engageRequestVersion
	// so the coordinator engages even if the panel was already open.
	openFromNotificationPeek: () => void;
	close: (trigger?: BizyPanelTrigger) => void;
	toggle: (trigger?: BizyPanelTrigger) => void;
	toggleFullscreen: () => void;
	exitFullscreen: () => void;
	showChat: () => void;
	showHistory: () => void;
	showSettings: (section?: BizySettingsSection) => void;
	closeHistoryDrawer: () => void;
	closeSettingsModal: () => void;
	setFullscreenDrawerWidth: (width: number) => void;
	setWidth: (width: number) => void;
	setIsResizing: (isResizing: boolean) => void;
	setSupportMode: (mode: BizySupportMode) => void;
	setSupportContext: (context: BizySupportContext) => void;
	resetSupport: () => void;
}

export const useBizySidePanelStore = create<BizySidePanelStore>((set) => ({
	isOpen: false,
	isFullscreen: false,
	lastTrigger: null,
	view: 'chat',
	isHistoryDrawerOpen: false,
	fullscreenHistoryDrawerOpen: true,
	isSettingsModalOpen: false,
	settingsSection: 'general',
	fullscreenDrawerWidth: BIZY_FULLSCREEN_DRAWER_DEFAULT_WIDTH,
	width: BIZY_PANEL_DEFAULT_WIDTH,
	isResizing: false,
	supportMode: 'bizy',
	supportContext: {},
	engageRequestVersion: 0,
	open: (trigger, options) =>
		set((state) => {
			const panelMode = getBizyPanelMode();
			const isFullscreen = options?.isFullscreen ?? panelMode === 'fullscreen';
			return {
				isOpen: true,
				isFullscreen,
				isHistoryDrawerOpen: isFullscreen ? (options?.isHistoryDrawerOpen ?? state.fullscreenHistoryDrawerOpen) : false,
				lastTrigger: trigger ?? 'user',
			};
		}),
	// Force view: 'chat' so the coordinator hook (which lives in
	// BizySidePanelChatView) actually mounts. Without this, a user who left
	// the panel on history/settings and closed it would re-open into that
	// stale view via peek and never see the proactive conversation surface.
	openFromNotificationPeek: () =>
		set((state) => {
			const isFullscreen = getBizyPanelMode() === 'fullscreen';
			return {
				isOpen: true,
				isFullscreen,
				lastTrigger: 'notification_peek',
				view: 'chat',
				isHistoryDrawerOpen: isFullscreen ? state.fullscreenHistoryDrawerOpen : false,
				isSettingsModalOpen: false,
				engageRequestVersion: state.engageRequestVersion + 1,
			};
		}),
	close: (trigger) =>
		set((state) => {
			// Keep a settings deep link (?bizy_settings=...) open through 'auto'
			// close/restore effects that run later in the same mount.
			if (trigger === 'auto' && state.isSettingsModalOpen && state.lastTrigger === BIZY_SETTINGS_DEEP_LINK_TRIGGER) return state;
			return { isOpen: false, isHistoryDrawerOpen: false, isSettingsModalOpen: false, lastTrigger: trigger ?? 'user' };
		}),
	toggle: (trigger) =>
		set((state) => {
			const isOpening = !state.isOpen;
			const isFullscreen = state.isOpen ? state.isFullscreen : getBizyPanelMode() === 'fullscreen';
			return {
				isOpen: isOpening,
				isFullscreen,
				isHistoryDrawerOpen: isOpening && isFullscreen ? state.fullscreenHistoryDrawerOpen : false,
				isSettingsModalOpen: state.isOpen ? false : state.isSettingsModalOpen,
				lastTrigger: trigger ?? 'user',
			};
		}),
	toggleFullscreen: () =>
		set((state) => {
			const next = !state.isFullscreen;
			return { isFullscreen: next, isHistoryDrawerOpen: next ? state.fullscreenHistoryDrawerOpen : false };
		}),
	exitFullscreen: () => set({ isFullscreen: false, isHistoryDrawerOpen: false }),
	showChat: () => set((state) => ({ view: 'chat', isHistoryDrawerOpen: state.isFullscreen ? state.isHistoryDrawerOpen : false, isSettingsModalOpen: false })),
	showHistory: () => set({ view: 'chat', isHistoryDrawerOpen: true, fullscreenHistoryDrawerOpen: true, isSettingsModalOpen: false }),
	showSettings: (settingsSection = 'general') => set({ view: 'chat', isSettingsModalOpen: true, settingsSection }),
	closeHistoryDrawer: () =>
		set((state) => ({ isHistoryDrawerOpen: false, fullscreenHistoryDrawerOpen: state.isFullscreen ? false : state.fullscreenHistoryDrawerOpen })),
	closeSettingsModal: () => set({ isSettingsModalOpen: false }),
	setFullscreenDrawerWidth: (width) =>
		set({ fullscreenDrawerWidth: Math.max(BIZY_FULLSCREEN_DRAWER_MIN_WIDTH, Math.min(BIZY_FULLSCREEN_DRAWER_MAX_WIDTH, width)) }),
	setWidth: (width) => set({ width: Math.max(BIZY_PANEL_MIN_WIDTH, Math.min(BIZY_PANEL_MAX_WIDTH, width)) }),
	setIsResizing: (isResizing) => set({ isResizing }),
	setSupportMode: (supportMode) => set({ supportMode }),
	setSupportContext: (supportContext) => set({ supportContext }),
	resetSupport: () => set({ supportMode: 'bizy', supportContext: {} }),
}));
